import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { X, CheckCircle2 } from 'lucide-react';
import { submitManualEntry } from '../../services/guardService';

export default function ManualEntryModal({ isOpen, onClose }) {
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm();
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const onSubmit = async (data) => {
    setError('');
    try {
      const payload = {
        ...data,
        gdprConsent: true,
        comments: data.notes
      };
      const res = await submitManualEntry(payload);
      if (res.success) {
        setResult({ success: true, name: data.fullName });
        reset();
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleClose = () => {
    setResult(null);
    setError('');
    reset();
    onClose();
  };

  return (
    <div className="modal-overlay" style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999, padding: '1rem'
    }}>
      <div className="modal-content" style={{
        background: 'var(--surface)', width: '100%', maxWidth: '450px',
        borderRadius: '12px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden'
      }}>
        <div className="modal-header" style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)'
        }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', color: 'var(--text-main)' }}>Entrée Manuelle</h2>
          <button onClick={handleClose} className="btn-icon" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body" style={{ padding: '1.5rem' }}>
          {result ? (
            <div style={{ textAlign: 'center', padding: '1rem 0' }}>
              <CheckCircle2 size={48} color="#10B981" style={{ margin: '0 auto 1rem' }} />
              <h3 style={{ margin: '0 0 0.5rem', color: 'var(--text-main)' }}>Entrée enregistrée</h3>
              <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
                {result.name} a été enregistré(e) avec succès.
              </p>
              <button className="btn btn-primary" onClick={() => setResult(null)} style={{ width: '100%' }}>
                Nouvelle entrée
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              {error && <div className="error-banner">{error}</div>}

              <div className="form-group">
                <label>Nom complet *</label>
                <input
                  {...register('fullName', { required: 'Le nom est requis' })}
                  placeholder="Nom du visiteur"
                  autoFocus
                />
                {errors.fullName && <span className="field-error">{errors.fullName.message}</span>}
              </div>

              <div className="form-group">
                <label>Téléphone *</label>
                <input
                  type="tel"
                  {...register('phone', { required: 'Le téléphone est requis', pattern: { value: /^\+?[0-9]{8,15}$/, message: '8-15 chiffres' } })}
                  placeholder="+216 XX XXX XXX"
                />
                {errors.phone && <span className="field-error">{errors.phone.message}</span>}
              </div>

              <div className="form-group">
                <label>Email (optionnel)</label>
                <input
                  type="email"
                  {...register('email')}
                  placeholder="votre@email.com"
                />
              </div>

              <div className="form-group">
                <label>3 derniers chiffres CIN *</label>
                <input
                  type="text"
                  maxLength={3}
                  {...register('cinLast3', { required: 'CIN est requis', pattern: { value: /^[0-9]{3}$/, message: 'Doit être 3 chiffres' } })}
                  placeholder="ex: 123"
                />
                {errors.cinLast3 && <span className="field-error">{errors.cinLast3.message}</span>}
              </div>

              <div className="form-group">
                <label>Motif de la visite *</label>
                <select
                  {...register('visitType', { required: 'Le motif est requis' })}
                >
                  <option value="">— Sélectionnez un motif —</option>
                  <option value="visiteur_simple">Visiteur simple</option>
                  <option value="reunion">Réunion / Rendez-vous</option>
                  <option value="fournisseur">Livraison / Fournisseur</option>
                  <option value="evenement">Événement / Formation</option>
                  <option value="commercial">Commercial</option>
                  <option value="etudiant">Étudiant</option>
                </select>
                {errors.visitType && <span className="field-error">{errors.visitType.message}</span>}
              </div>

              <div className="form-group">
                <label>Remarques (optionnel)</label>
                <textarea
                  {...register('notes')}
                  placeholder="Informations supplémentaires"
                  rows={3}
                  style={{ resize: 'vertical' }}
                />
              </div>

              <div className="form-actions" style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={handleClose}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'En cours...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
